var AnimationSupport = require("enyo-anim/AnimationSupport"),
    Button           = require("enyo/Button"),
    dispatcher       = require("enyo/dispatcher"),
    kind             = require("enyo/kind"),
    logger           = require("enyo/logger"),
    ready            = require("enyo/ready");

require("enyo-ui");

// Make sure enyo listens to contextmenu event.
ready(function() {
    dispatcher.listen(document, "contextmenu");
});

/**
 * [ScrollButton]{@link enyo-ui.ScrollButton} is a button that - when pressed -
 * sets and moves a target position for a [Scroller]{@link enyo-ui.Scroller} to
 * animate towards, causing the scroller to accelerate in the provided
 * direction.
 *
 * Once the button is released the scrollers target position will be left in
 * place leaving the scroller to either gradually stop at the target.
 *
 * The longer the button is pressed, the faster the target position is moved,
 * increasing the speed at which the scroller will move as a result.
 *
 * @ui
 * @class   enyo-ui.ScrollButton
 * @extends enyo.Button
 * @mixes   enyo-anim.AnimationSupport
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.ScrollButton.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollButton",

    /**
     * @private
     */
    kind: Button,

    /**
     * @private
     */
    mixins: [AnimationSupport],

    /**
     * @private
     */
    classes: "enyo-ui-scroll-button",

    /**
     * The scroller we're currently working with.
     *
     * @type    {?enyo-ui.Scroller}
     * @default null
     * @private
     */
    scroller: null,

    /**
     * Flags whether the ScrollButton is scrolling the scroller.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    scrolling: false,

    /**
     * The current speed at which we move the scroll target, in pixels per
     * frame.
     *
     * @type {number}
     * @readonly
     * @public
     */
    speed: 0,

    /**
     * @private
     */
    shouldIgnoreTap: false,

    /**
     * Set scroll direction to "up", "down", "left", "right" or `null`.
     *
     * @type    {?string}
     * @default null
     * @public
     */
    direction: null,

    /**
     * The smallest speed at which to move the scroll target, in pixels per
     * frame.
     *
     * @type    {number}
     * @default 1
     * @public
     */
    minSpeed: 5,

    /**
     * The maximum speed at which to move the scroll target, in pixels per
     * frame.
     *
     * @type    {number}
     * @default 10
     * @public
     */
    maxSpeed: 20,

    /**
     * The multiplier that accelerates scroll speed each frame.
     *
     * @type {number}
     * @default 1.05
     * @public
     */
    acceleration: 1.05,

    /**
     * The factor of scroller width/height to scroll in one pageing
     * transition.
     *
     * @type    {number}
     * @default 0.9
     * @public
     */
    pagingFactor: 0.9,

    /**
     * @private
     */
    handlers: {
        ondown:        "ondown",
        onhold:        "onhold",
        oncontextmenu: "oncontextmenu",
        onup:          "onup",
        ontap:         "ontap"
    },

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.scroll = this.bindSafely(this.scroll);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.scrollStop();
        };
    }),

    /**
     * Start scrolling. Prevent simulated right-click on touch devices. Prevent
     * tap event.
     *
     * @private
     */
    ondown: function() {
        this.scrollStart();
    },

    /**
     * Don't tap once a hold gesture is clearly encountered.
     *
     * @private
     */
    onhold: function() {
        this.set("shouldIgnoreTap", true);
    },

    /**
     * Prevent context-menu from showing up during touch-and-hold.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    oncontextmenu: function(sender, event) {
        this.scrolling && event.preventDefault();
    },

    /**
     * Stop scrolling.
     *
     * @private
     */
    onup: function() {
        this.scrollStop();
    },

    /**
     * Scroll a full page on tap.
     *
     * @private
     */
    ontap: function() {
        if (!this.shouldIgnoreTap) {
            this.page();
        }
        else {
            this.set("shouldIgnoreTap", false);
        }
    },

    /**
     * Set a css class to match the scroll direction.
     *
     * @param {string}  oldDirection  The old direction
     * @param {string}  newDirection  The new direction
     * @private
     */
    directionChanged: function(oldDirection, newDirection) {
        switch (oldDirection) {
            case "up":
            case "down":
            case "left":
            case "right":
                this.removeClass("enyo-ui-scroll-button--scroll-direction-" + oldDirection);
                break;
            case null:
                this.removeClass("enyo-ui-scroll-button--scroll-direction-none");
                break;
            default:
                break;
        }

        switch (newDirection) {
            case "up":
            case "down":
            case "left":
            case "right":
                this.addClass("enyo-ui-scroll-button--scroll-direction-" + newDirection);
                break;
            case null:
                this.addClass("enyo-ui-scroll-button--scroll-direction-none");
                break;
            default:
                logger.error("Invalid scroll direction: " + newDirection);
                break;
        }
    },

    /**
     * If the scroller already scrolled all the way to the limit of the current
     * scroll direction, we should not scroll.
     *
     *
     * @return {boolean}  `true` if we should scroll, `false` otherwise
     * @private
     */
    shouldScroll: function() {
        var scroller  = this.scroller,
            direction = this.direction;

        // Already scrolled to it's limits
        if (!scroller
            || direction === null
            || (direction === "up" && scroller.isAtTop())
            || (direction === "down" && scroller.isAtBottom())
            || (direction === "left" && scroller.isAtLeft())
            || (direction === "right" && scroller.isAtRight())
        ) {
            return false;
        }

        return true;
    },

    /**
     * Starts scrolling.
     *
     * @public
     */
    scrollStart: function() {
        var scroller = this.scroller;

        // Already scrolling or nothing to scroll
        if (!scroller || this.scrolling || !this.shouldScroll()) {
            return;
        }

        // Mark as scrolling
        this.set("scrolling", true);

        // Capture any up event, because it may happen outside of the button.
        dispatcher.capture(this, {
            onup: "scrollStop"
        });

        this.scroll();
    },

    /**
     * Move the scroll target.
     *
     * @private
     */
    scroll: function() {
        var scroller     = this.scroller,
            direction    = this.direction,
            speed        = this.speed,
            minSpeed     = this.minSpeed,
            maxSpeed     = this.maxSpeed,
            acceleration = this.acceleration;

        // Only during scroll
        if (!this.scrolling) {
            this.warn("Don't call `scroll` if control is not scrolling.");

            return;
        }

        // Nothing to scroll
        if (!scroller || !this.shouldScroll()) {
            this.scrollStop();

            return;
        }

        // Calculate speed
        speed = Math.max(minSpeed, Math.min(speed * acceleration, maxSpeed));
        this.set("speed", speed);

        // Get current scroll target
        switch (direction) {
            case "up":
            case "down":
                scroller.extendScrollBy(
                    undefined,
                    direction === "up" ? -speed : speed
                );
                break;
            case "left":
            case "right":
                scroller.extendScrollBy(
                    direction === "left" ? -speed : speed,
                    undefined
                );
                break;
            default:
                logger.error("Invalid scroll direction: " + direction);
                break;
        }

        // Schedule next frame as needed
        if (this.shouldScroll()) {
            this.requestAnimationFrame("scroll", this.scroll);

            return;
        }

        // And we're done!
        this.scrollStop();
    },

    /**
     * Stops scrolling.
     *
     * @public
     */
    scrollStop: function() {
        dispatcher.release(this);
        this.cancelAnimationFrame("scroll");
        this.set("speed", 0);
        this.set("scrolling", false);
    },

    /**
     * Set a scroll target for a full page ahead in the current direction.
     *
     * @private
     */
    page: function() {
        var scroller     = this.scroller,
            direction    = this.direction,
            pagingFactor = this.pagingFactor,
            dimensions;

        // Nothing to scroll
        if (!scroller || !this.shouldScroll()) {
            this.scrollStop();

            return;
        }

        dimensions = scroller.dimensions;

        // Get current scroll target
        switch (direction) {
            case "up":
                scroller.extendScrollBy(
                    undefined,
                    dimensions.scrollerHeight * pagingFactor * -1
                );
                break;
            case "down":
                scroller.extendScrollBy(
                    undefined,
                    dimensions.scrollerHeight * pagingFactor
                );
                break;
            case "left":
                scroller.extendScrollBy(
                    dimensions.scrollerWidth * pagingFactor * -1,
                    undefined
                );
                break;
            case "right":
                scroller.extendScrollBy(
                    dimensions.scrollerWidth * pagingFactor,
                    undefined
                );
                break;
            default:
                logger.error("Invalid scroll direction: " + direction);
                break;
        }
    },

    /**
     * Determines if control is scrolling.
     *
     * @return {boolean}  True if scrolling, False otherwise.
     * @public
     */
    isScrolling: function() {
        return this.scrolling;
    },

    /**
     * Stop scroll when node gets removed from DOM.
     *
     * @method
     * @private
     */
    removeNodeFromDom: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.scrollStop();
        };
    }),

    /**
     * Stop scroll when control is no longer generated (implying it does not
     * have a DOM Node anymore).
     *
     * @method
     * @private
     */
    generatedChanged: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            if (!this.generated && this.scrolling) {
                this.scrollStop();
            }
        };
    }),

    /**
     * Stop scrolling when button gets hidden.
     *
     * @method
     * @private
     */
    absoluteShowingChanged: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            if (!this.absoluteShowing && this.scrolling) {
                this.scrollStop();
            }
        };
    }),

    /**
     * Stop scrolling when button gets disabled.
     *
     * @private
     */
    disabledChanged: function() {
        if (this.disabled && this.scrolling) {
            this.scrollStop();
        }
    }

});
